import React, { useState, useEffect, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { LanguageProvider } from "@/hooks/useLanguage";
import { ContentOverridesProvider } from "@/hooks/useContentOverrides";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router';
import { CookieConsent } from "@/components/CookieConsent";
import { FirstSignInChoices } from "@/components/consent/FirstSignInChoices";
import { OfflineBanner } from "@/components/OfflineBanner";
import { HelpWidget } from "@/components/HelpWidget";
import { AuthWrapper } from "@/components/AuthWrapper";
import { InvestorDemoBanner } from "@/components/InvestorDemoBanner";
import { StagingBanner } from '@/components/StagingBanner';
import { MarketNotice } from '@/components/MarketNotice';
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { wireNativeOnlineManager } from "@/hooks/useOnlineStatus";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import { useAnalyticsIdentity } from "@/hooks/useAnalyticsIdentity";
import { useFirstTouchAttribution } from "@/hooks/useFirstTouchAttribution";
import { initOpenPanel } from "@/lib/analytics/openpanel";
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ModeSwitchProvider } from '@/components/animations/ModeSwitchProvider';
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";
// Eager: Index is the LCP landing page, Auth is the most common entry point,
// NotFound is the tiny catch-all fallback. Everything else is code-split.
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import { isNative } from "@/lib/env";
import { hydrateBookingDraft } from "@/lib/bookingPersistence";
import { clearSharedFiles } from "@/lib/sharedFiles";
import { loadBookingFlowPage } from "@/lib/bookingFlowRoute";
// Native bundle (kanfy-mobile): every route the mobile assessment (§F) classes
// as desktop-only or not-bundled resolves to an empty element, so its chunk
// leaves Rollup's graph and never reaches the APK. manualChunks cannot do
// this — it only names chunks — and the guard has to sit on the import, the
// same way UAT is gated below. `isNative` is a build-time constant.
// An excluded route resolves to NativeExcludedPage rather than to nothing:
// landing on a blank screen reads as a broken app, whereas that page says the
// section lives on the website and offers the way there. It is a static
// import target, so the excluded page's own chunk still leaves the graph.
const EmptyRoute = () => <></>;
type Loaded<T> = Promise<{ default: T | typeof EmptyRoute }>;
const nativeExcluded = <T,>(load: () => Promise<{ default: T }>): Loaded<T> =>
  isNative ? (import('./mobile/NativeExcludedPage') as Loaded<T>) : load();
// Marketing / static content pages — lazy so they stay out of the entry chunk.
const ServiceDetails = lazy(() => import("./pages/ServiceDetails"));
const BecomeHelper = lazy(() => import("./pages/BecomeHelper"));
const ServiceStandards = lazy(() => nativeExcluded(() => import("./pages/ServiceStandards")));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const About = lazy(() => import("./pages/About"));
const PlatformRules = lazy(() => nativeExcluded(() => import("./pages/PlatformRules")));
const Support = lazy(() => import("./pages/Support"));
const Safety = lazy(() => nativeExcluded(() => import("./pages/Safety")));
const FAQPage = lazy(() => nativeExcluded(() => import("./pages/FAQPage")));
const Guides = lazy(() => nativeExcluded(() => import("./pages/Guides")));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
// Bundled in the app like /data-deletion: the page the deletion email links to.
const DataDeletionConfirm = lazy(() => import("./pages/DataDeletionConfirm"));
const Services = lazy(() => import("./pages/Services"));
const AllServices = lazy(() => import("./pages/AllServices"));
// Lazy-loaded app pages (not in the critical above-fold path)
const FindServices = lazy(() => import("./pages/FindServices"));
// Job-board subsystem removed (Jobs/PostJob/JobDetails/ApplicantReview/
// FindJobs/MyApplications deleted; recoverable from git history). Its old
// URLs still redirect home. See the /jobs route note below.
const Blog = lazy(() => nativeExcluded(() => import("./pages/Blog")));
const BlogPost = lazy(() => nativeExcluded(() => import("./pages/BlogPost")));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const HelperProfileCreation = lazy(() => import("./pages/HelperProfileCreation"));
const ClientProfileCreation = lazy(() => import("./pages/ClientProfileCreation"));
const ProfileCreationRouter = lazy(() => import("./pages/ProfileCreationRouter"));
const DashboardRouter = lazy(() => import("./pages/DashboardRouter"));
const HelperDashboard = lazy(() => import("./pages/HelperDashboard"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const HelperAccount = lazy(() => import("./pages/HelperAccount"));
const ClientAccount = lazy(() => import("./pages/ClientAccount"));
const LinkWhatsApp = lazy(() => import("./pages/LinkWhatsApp"));
const VerifyIdentity = lazy(() => import("./pages/VerifyIdentity"));
const RequestService = lazy(() => import("./pages/RequestService"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const MyHelpers = lazy(() => import("./pages/MyHelpers"));
const PostAuthRedirect = lazy(() => import("./pages/PostAuthRedirect"));
const Messages = lazy(() => import("./pages/Messages"));
const Notifications = lazy(() => import("./pages/Notifications"));
// In the app, start loading the booking draft from the secure store at boot;
// the flow's route waits for it (see bookingFlowRoute.ts).
if (isNative) void hydrateBookingDraft();
// Files the last run wrote for the share sheet go at every start (M11 P2).
if (isNative) void clearSharedFiles();
const BookingFlow = lazy(loadBookingFlowPage);
const PaymentComplete = lazy(() => import("./pages/PaymentComplete"));
// Dev-only test pages — gate the import itself so esbuild DCEs them (and their
// chunks) out of production builds entirely, not just the routes below.
const AddressPickerTest = import.meta.env.DEV ? lazy(() => import("./pages/dev/AddressPickerTest")) : (() => null);
import LegacyBookingRedirect from "./components/booking/LegacyBookingRedirect";
const HelperProfile = lazy(() => import("./pages/HelperProfile"));
const BrowseHelpers = lazy(() => import("./pages/BrowseHelpers"));
const AdminUtils = lazy(() => nativeExcluded(() => import("./pages/AdminUtils")));
const AdminSettings = lazy(() => nativeExcluded(() => import("./pages/AdminSettings")));
const HelperMyServices = lazy(() => import("./pages/HelperMyServices"));
// Service sub-pages (accessed only after browsing, not above the fold)
const ChildcareService = lazy(() => import("./pages/services/ChildcareService"));
const ElderlyCarePage = lazy(() => import("./pages/services/ElderlyCarePage"));
const CleaningService = lazy(() => import("./pages/services/CleaningService"));
const TutoringService = lazy(() => import("./pages/services/TutoringService"));
const PetCareService = lazy(() => import("./pages/services/PetCareService"));
const SecurityService = lazy(() => import("./pages/services/SecurityService"));
const MaintenanceService = lazy(() => import("./pages/services/MaintenanceService"));
const GardeningService = lazy(() => import("./pages/services/GardeningService"));
import { ProtectedRoute, HelperRoute } from "./components/ProtectedRoute";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { resolveAccountRole, accountDestination } from "@/lib/accountRouting";
import { logger } from "@/lib/logger";
import { PerformanceMonitor } from '@/components/PerformanceMonitor';
const Logout = lazy(() => import("./pages/Logout"));
const ShareStory = lazy(() => nativeExcluded(() => import("./pages/ShareStory")));
// Where an author confirms a hidden legacy story may be published again
// (owner, 18 Oct; 20270497000000). Website-only, like /share-story.
const StoryConsent = lazy(() => nativeExcluded(() => import("./pages/StoryConsent")));
const Contact = lazy(() => nativeExcluded(() => import("./pages/Contact")));
const StubPage = lazy(() => import("./pages/StubPage"));
const HelperResources = lazy(() => nativeExcluded(() => import("./pages/HelperResources")));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const CommunityGuidelines = lazy(() => nativeExcluded(() => import("./pages/CommunityGuidelines")));
const Careers = lazy(() => nativeExcluded(() => import("./pages/Careers")));
const OperationalRegions = lazy(() => nativeExcluded(() => import("./pages/admin/OperationalRegions")));
const AdminServiceLocations = lazy(() => nativeExcluded(() => import("./pages/admin/ServiceLocations")));
const AdminIntegrityDashboard = lazy(() => nativeExcluded(() => import("./pages/admin/IntegrityDashboard")));
import { BottomNav } from "@/components/BottomNav";
import { MobileShell } from "@/mobile/MobileShell";
import { WebUpdateRequiredPrompt } from "@/components/WebUpdateRequiredPrompt";
import { ErrorBoundaryWrapper } from "@/components/ErrorBoundaryWrapper";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LoadingFallback } from "@/components/shell/LoadingFallback";
// Gate the UAT bundle. `import.meta.env.VITE_ENABLE_UAT` is statically replaced
// at build time, so when it is not 'true' esbuild dead-code-eliminates the
// import() below and no UATPortal/uat-* chunks are emitted. (The previous
// @vite-ignore actually DEFEATED this — it kept the import external so the
// chunks shipped to production regardless. UAT now lives in its own deployment
// built with VITE_ENABLE_UAT=true against a separate, non-prod Supabase project.)
const UAT_ENABLED = import.meta.env.VITE_ENABLE_UAT === 'true';
const UATPortal = lazy(() =>
  UAT_ENABLED
    ? import('./pages/UATPortal')
    : Promise.resolve({ default: EmptyRoute })
);

// Lazy-load heavy admin pages for faster initial load
const AdminLogin = lazy(() => nativeExcluded(() => import("./pages/admin/AdminLogin")));
const UATResults = lazy(() =>
  UAT_ENABLED
    ? import("./pages/admin/UATResults")
    : Promise.resolve({ default: EmptyRoute })
);
const AdminSetup = lazy(() => nativeExcluded(() => import("./pages/admin/AdminSetup")));
const AdminLayout = lazy(() => nativeExcluded(() => import("./pages/admin/AdminLayout")));
const AdminDashboard = lazy(() => nativeExcluded(() => import("./pages/admin/AdminDashboard")));
const Analytics = lazy(() => nativeExcluded(() => import("./pages/admin/Analytics")));
const Customers = lazy(() => nativeExcluded(() => import("./pages/admin/Customers")));
const TasksBookings = lazy(() => nativeExcluded(() => import("./pages/admin/TasksBookings")));
const Financial = lazy(() => nativeExcluded(() => import("./pages/admin/Financial")));
const Transactions = lazy(() => nativeExcluded(() => import("./pages/admin/Transactions")));
const HelpersList = lazy(() => nativeExcluded(() => import("./pages/admin/helpers/HelpersList")));
const HelperVisibility = lazy(() => nativeExcluded(() => import("./pages/admin/HelperVisibility")));
const HelperDetail = lazy(() => nativeExcluded(() => import("./pages/admin/helpers/HelperDetail")));
const HelperKYC = lazy(() => nativeExcluded(() => import("./pages/admin/helpers/HelperKYC")));
const Safeguarding = lazy(() => nativeExcluded(() => import("./pages/admin/Safeguarding")));
const Reports = lazy(() => nativeExcluded(() => import("./pages/admin/Reports")));
const Settings = lazy(() => nativeExcluded(() => import("./pages/admin/Settings")));
const AccountSecurity = lazy(() => nativeExcluded(() => import("./pages/admin/AccountSecurity")));
const Payouts = lazy(() => nativeExcluded(() => import("./pages/admin/Payouts")));
const Ledgers = lazy(() => nativeExcluded(() => import("./pages/admin/Ledgers")));
const PromoCodes = lazy(() => nativeExcluded(() => import("./pages/admin/PromoCodes")));
const AuditLogs = lazy(() => nativeExcluded(() => import("./pages/admin/AuditLogs")));
const SystemHealth = lazy(() => nativeExcluded(() => import("./pages/admin/SystemHealth")));
const DataSeeding = lazy(() => nativeExcluded(() => import("./pages/admin/DataSeeding")));
const FunnelAnalytics = lazy(() => nativeExcluded(() => import("./pages/admin/FunnelAnalytics")));
const DailyReport = lazy(() => nativeExcluded(() => import("./pages/admin/DailyReport")));
const ErrorLogs = lazy(() => nativeExcluded(() => import("./pages/admin/ErrorLogs")));
const AdminSupport = lazy(() => nativeExcluded(() => import("./pages/admin/Support")));
const Disputes = lazy(() => nativeExcluded(() => import("./pages/admin/Disputes")));
const ApprovalsHub = lazy(() => nativeExcluded(() => import("./pages/admin/ApprovalsHub")));
const AutomationMonitor = lazy(() => nativeExcluded(() => import("./pages/admin/AutomationMonitor")));
const AiAgents = lazy(() => nativeExcluded(() => import("./pages/admin/AiAgents")));
const Management = lazy(() => nativeExcluded(() => import("./pages/admin/Management")));
const PrivacyRegister = lazy(() => nativeExcluded(() => import("./pages/admin/PrivacyRegister")));
const HelpArticles = lazy(() => nativeExcluded(() => import("./pages/admin/HelpArticles")));
const Categories = lazy(() => nativeExcluded(() => import("./pages/admin/Categories")));
const AdminImages = lazy(() => nativeExcluded(() => import("./pages/admin/Images")));
const PricingStrategy = lazy(() => nativeExcluded(() => import("./pages/admin/PricingStrategy")));
const RefundPolicy = lazy(() => nativeExcluded(() => import("./pages/admin/RefundPolicy")));
const LoyaltyCost = lazy(() => nativeExcluded(() => import('./pages/admin/LoyaltyCost')));
const OutcomeTemplates = lazy(() => nativeExcluded(() => import("./pages/admin/OutcomeTemplates")));
const ContentTranslations = lazy(() => nativeExcluded(() => import("./pages/admin/ContentTranslations")));
const DocumentRenewals = lazy(() => nativeExcluded(() => import("./pages/admin/DocumentRenewals")));
const MediaScreening = lazy(() => nativeExcluded(() => import("./pages/admin/MediaScreening")));
const WorkPhotoModeration = lazy(() => nativeExcluded(() => import("./pages/admin/WorkPhotoModeration")));
const Appeals = lazy(() => nativeExcluded(() => import("./pages/admin/Appeals")));
const ProfileReports = lazy(() => nativeExcluded(() => import("./pages/admin/ProfileReports")));
const CrossBorderProviders = lazy(() => nativeExcluded(() => import("./pages/admin/CrossBorderProviders")));
const AdminReviews = lazy(() => nativeExcluded(() => import("./pages/admin/Reviews")));
const ReviewQueue = lazy(() => nativeExcluded(() => import("./pages/admin/ReviewQueue")));
const AdminAppFeedback = lazy(() => nativeExcluded(() => import("./pages/admin/AppFeedback")));
const TrustSafetyReports = lazy(() => nativeExcluded(() => import("./pages/admin/TrustSafetyReports")));
const ClientTrustFlags = lazy(() => nativeExcluded(() => import("./pages/admin/ClientTrustFlags")));

// Legacy /profile link uses the same role decision as the dashboard.
function ProfileRedirect() {
  const { user, isLoading } = useAuth();
  const [dest, setDest] = useState<string | null>(null);
  useEffect(() => {
    if (isLoading || !user) return;
    let cancelled = false;
    (async () => {
      let to = "/client-account";
      try {
        const [profileResult, rolesResult] = await Promise.all([
          supabase.from("profiles").select("role, is_helper, profile_status").eq("id", user.id).maybeSingle(),
          supabase.from("user_roles").select("role").eq("user_id", user.id),
        ]);
        if (profileResult.error) throw profileResult.error;
        const roles = (rolesResult.data ?? []).map((row) => row.role);
        to = accountDestination(
          resolveAccountRole(profileResult.data, roles, user.user_metadata),
          profileResult.data,
          'profile',
        );
      } catch {
        // Preserve the existing client fallback on a failed profile lookup.
      }
      if (!cancelled) setDest(to);
    })();
    return () => { cancelled = true; };
  }, [user, isLoading]);
  if (isLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!dest) return null;
  return <Navigate to={dest} replace />;
}

// /my-bookings/:id has no dedicated detail page — MyBookings is a list view —
// but notification links (e.g. "leave a review") point at /my-bookings/{id}?review=true.
// Redirect to the list while preserving the booking id + query string as
// query params, instead of silently dropping them.
function MyBookingsRedirect() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  if (id) params.set('bookingId', id);
  return <Navigate to={`/my-bookings?${params.toString()}`} replace />;
}

// Issue #359: a bare <Navigate to="/x" /> drops the query string, which broke
// notification deep-links routed through legacy paths (e.g.
// /helper-dashboard?tab=requests lost its tab). Preserve search + hash.
function RedirectPreservingQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

// Component that contains routes and session timeout logic
const AppContent = () => {
  const { pathname } = useLocation();
  const { showWarning, remainingTime, continueSession, handleLogout } = useSessionTimeout();
  // Throttled "last seen" heartbeat while the tab is visible (presence).
  usePresenceHeartbeat();
  // Analytics: start the SDK itself, once. Until this ran, the instance was
  // only ever constructed by an explicit track/identify call, so the SDK's own
  // screen_view listener began mid-session (or never, for someone who browses
  // without signing in) and the "consent granted later" listener was never
  // registered. Both this and the identify below are no-ops without a client
  // id or without analytics consent.
  useEffect(() => {
    initOpenPanel();
  }, []);
  // Analytics: identify + market group for the signed-in person (consent-gated).
  useAnalyticsIdentity();
  // Where this visit came from, sent once at sign-in (consent-gated).
  useFirstTouchAttribution(isNative ? "android" : "web");

  return (
    <>
      {/* Skip-to-content: first focusable element for keyboard/screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:font-medium focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>
<StagingBanner />
            <InvestorDemoBanner />
            {/* Mounted app-wide, not per page: an overridden market changes
                the currency, the city list and the site copy everywhere, so
                the disclosure has to be everywhere too. Renders nothing
                unless the viewer is actually outside their own market. */}
            <MarketNotice />
      <SessionTimeoutWarning
        open={showWarning}
        remainingTime={remainingTime}
        onContinue={continueSession}
        onLogout={handleLogout}
      />
      {/* The Android build gets the shell (tab stacks, system back, the
          minimum-version gate); the web keeps BottomNav. `isNative` is a
          compile-time constant, so the web bundle carries no shell and no
          Play Store screen, and the app carries no BottomNav. */}
      {isNative ? <MobileShell /> : <BottomNav />}
      {/* The web's answer to a KA001 refusal: a reload prompt, never the
          Play Store. Renders nothing until a response carries KA001. */}
      {!isNative && <WebUpdateRequiredPrompt />}
      <Toaster />
      <Sonner />
      <ErrorBoundaryWrapper componentName="AppRoutes">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/link/whatsapp" element={<LinkWhatsApp />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/post-auth" element={<PostAuthRedirect />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/profile-creation" element={
            <ProtectedRoute requireCompleteProfile={false}>
              <ProfileCreationRouter />
            </ProtectedRoute>
          } />
          <Route path="/helper-profile" element={
            <ProtectedRoute requireCompleteProfile={false}>
              <HelperProfileCreation />
            </ProtectedRoute>
          } />
          <Route path="/client-profile" element={
            <ProtectedRoute requireCompleteProfile={false}>
              <ClientProfileCreation />
            </ProtectedRoute>
          } />
          <Route path="/find-services" element={<FindServices />} />
                  <Route path="/find-helpers" element={<Navigate to="/booking-flow" replace />} />
          <Route path="/helper/bookings" element={
            <HelperRoute>
              <HelperDashboard defaultTab="requests" />
            </HelperRoute>
          } />
          <Route path="/helper/:helperId" element={<HelperProfile />} />
          <Route path="/browse-helpers" element={
            <ProtectedRoute>
              <BrowseHelpers />
            </ProtectedRoute>
          } />
          <Route path="/services" element={<Services />} />
          <Route path="/services/all" element={<AllServices />} />
          {/* Role-based dashboards */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          } />
          <Route path="/helper-dashboard" element={<RedirectPreservingQuery to="/dashboard/helper" />} />
          {/* Helper-only surfaces require helper capability (is_helper===true).
              This blocks pure clients and admins-without-helper-capability with
              a clean AccessDenied. Client surfaces stay auth-only because the
              dual-role model lets any helper also act as a client. */}
          <Route path="/helper/my-services" element={<HelperRoute><HelperMyServices /></HelperRoute>} />
          <Route path="/dashboard/helper" element={
            <ErrorBoundaryWrapper componentName="HelperDashboard">
              <HelperRoute>
                <HelperDashboard />
              </HelperRoute>
            </ErrorBoundaryWrapper>
          } />
          <Route path="/helper-account" element={
            <HelperRoute>
              <HelperAccount />
            </HelperRoute>
          } />
          <Route path="/client-account" element={
            <ProtectedRoute>
              <ClientAccount />
            </ProtectedRoute>
          } />
          <Route path="/verify-identity" element={
            <ProtectedRoute>
              <VerifyIdentity />
            </ProtectedRoute>
          } />
          <Route path="/client-dashboard" element={
            <ErrorBoundaryWrapper componentName="ClientDashboard">
              <ProtectedRoute>
                <ClientDashboard />
              </ProtectedRoute>
            </ErrorBoundaryWrapper>
          } />
          <Route path="/request-service" element={<RequestService />} />
          <Route path="/booking-flow" element={
            <ErrorBoundaryWrapper componentName="BookingFlow">
              <BookingFlow />
            </ErrorBoundaryWrapper>
          } />
          <Route path="/booking/payment-complete" element={
            <ErrorBoundaryWrapper componentName="PaymentComplete">
              <PaymentComplete />
            </ErrorBoundaryWrapper>
          } />
          {/* Dev-only manual test pages (sandbox cards, no auth). `import.meta.env.DEV`
              is a build-time literal, so esbuild dead-code-eliminates these routes —
              and the chunks — from production builds entirely. Never expose in prod. */}
          {import.meta.env.DEV && (
            <>
              <Route path="/dev/address-picker-test" element={
                <ErrorBoundaryWrapper componentName="AddressPickerTest">
                  <AddressPickerTest />
                </ErrorBoundaryWrapper>
              } />
            </>
          )}
          {/* Helper-first booking retired → task-first flow (booking-journey-remake).
              Old /booking/:helperId links forward into /booking-flow with the
              helper pre-seeded. */}
          <Route path="/booking/:helperId" element={<LegacyBookingRedirect />} />
          <Route path="/my-bookings" element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          } />
                    <Route path="/my-bookings/:id" element={<MyBookingsRedirect />} />
          {/* /my-applications — part of the archived job-board subsystem; redirected home (see /jobs note below). */}
          <Route path="/my-applications" element={<Navigate to="/" replace />} />
          {/* The bell's "View all →" has pointed here since it was written;
              without this route it landed on NotFound (M8). */}
          <Route path="/notifications" element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } />
          <Route path="/my-helpers" element={
            <ProtectedRoute>
              <MyHelpers />
            </ProtectedRoute>
          } />
          {/*
            /favorites was a third, disconnected favourites store: it read a demo
            fixture that folds to [] in non-demo builds, so it rendered "No saved
            helpers yet" to every user forever, and nothing in the app linked to
            it. Redirected rather than deleted so any existing bookmark still
            lands somewhere real — /my-helpers is where the Header's "Favourite
            Helpers" entry already points (#674).
          */}
          <Route path="/favorites" element={<Navigate to="/my-helpers" replace />} />
          {/* Legacy dashboard for backward compatibility */}
          <Route path="/legacy-dashboard" element={<Navigate to="/dashboard" replace />} />
          {/*
            /onboarding means HELPER onboarding, and that is deliberate (#786).
            It reads like a role-neutral route and is not, so it is stated here
            rather than left to be rediscovered.

            Both remaining callers already know the role when they use it:
              ProtectedRoute — gated on `is_helper && requiredUserType === 'helper'`
              BecomeHelper   — the become-a-helper page itself
            The caller that did NOT know — PostAuthRedirect's fallback for an
            authenticated user with no role — used to come here too, and so
            dropped people into the helper application, KYC and all. That one
            now goes to /profile-creation — the role chooser.

            /helper-onboarding is the same target under a name that cannot be
            misread; prefer it in new code.
          */}
          <Route path="/onboarding" element={<Navigate to="/helper-profile" replace />} />
          <Route path="/helper-onboarding" element={<Navigate to="/helper-profile" replace />} />
          <Route path="/client-onboarding" element={<Navigate to="/client-profile" replace />} />
          <Route path="/messages" element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          } />
          <Route path="/messages/:conversationId" element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          } />
          <Route path="/services/:category" element={<ServiceDetails />} />
          <Route path="/become-helper" element={<BecomeHelper />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/service-standards" element={<ServiceStandards />} />
          <Route path="/safety-guide" element={<Navigate to="/service-standards" replace />} />
          {/* ── Job-board subsystem REMOVED ──────────────────────────────
              The two-sided job board (post a job → review applicants; browse
              → apply) was retired, and its page components were deleted (they
              remain in git history). Every old entry URL still redirects home
              so bookmarked links do not 404. Paths are kept explicit (not a
              /jobs/* wildcard) so they stay 1:1 with ROUTE_REGISTRY and the
              #329 drift-guard test. */}
          <Route path="/jobs" element={<Navigate to="/" replace />} />
          <Route path="/post-job" element={<Navigate to="/" replace />} />
          <Route path="/jobs/:jobId" element={<Navigate to="/" replace />} />
          <Route path="/jobs/:jobId/applicants" element={<Navigate to="/" replace />} />
          
          {/* Service-specific landing pages */}
          <Route path="/childcare" element={<ChildcareService />} />
          <Route path="/elderly-care" element={<ElderlyCarePage />} />
          <Route path="/cleaning" element={<CleaningService />} />
          <Route path="/tutoring" element={<TutoringService />} />
          <Route path="/pet-care" element={<PetCareService />} />
          <Route path="/security" element={<SecurityService />} />
          <Route path="/maintenance" element={<MaintenanceService />} />
          <Route path="/gardening" element={<GardeningService />} />
          <Route path="/cooking" element={<Navigate to="/services/cooking" replace />} />
          <Route path="/driving" element={<Navigate to="/services/driving" replace />} />
          <Route path="/plumbing" element={<Navigate to="/services/plumbing" replace />} />

          {/* Footer link pages */}
          <Route path="/about" element={<About />} />
          <Route path="/platform-rules" element={<PlatformRules />} />
          <Route path="/support" element={<Support />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/data-deletion/confirm" element={<DataDeletionConfirm />} />
          
          {/* Legacy Admin Utils - Keep for backward compatibility.
              #471: "legacy"/"utils" imply high blast radius (raw data tools,
              settings bypasses) — default to super_admin only. */}
          <Route path="/admin-utils" element={
            <ProtectedAdminRoute routeKey="admin-utils">
              <AdminUtils />
            </ProtectedAdminRoute>
          } />
          <Route path="/admin/legacy-settings" element={
            <ProtectedAdminRoute routeKey="legacy-settings">
              <AdminSettings />
            </ProtectedAdminRoute>
          } />
          {/* Legacy approvals URL → canonical, admin-guarded Approvals Hub.
              The old AdminProfileApproval page rendered under a client-level
              ProtectedRoute and self-denied (its role check only matched the
              literal 'admin' role, so super_admins failed and were bounced to
              /dashboard → the client shell). Redirect to the properly guarded
              hub so admin access works and non-admins get a clean denial. */}
          <Route path="/admin/approvals" element={<Navigate to="/admin/approvals-hub" replace />} />
          
          {/* New Admin Portal Routes */}
          <Route path="/dashboard/admin" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          {/* #471/#894: the outer /admin wrapper has no routeKey — it renders
              only the AdminLayout shell (sidebar + outlet) and admits anyone
              the console admits. Every nested page below, the dashboard
              included, carries its own routeKey, checked against the server's
              allowed_admin_routes(); that is the actual access boundary. */}
          <Route path="/admin" element={
            <ErrorBoundaryWrapper componentName="AdminLayout">
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            </ErrorBoundaryWrapper>
          }>
            <Route index element={
              <ProtectedAdminRoute routeKey="index">
                <AdminDashboard />
              </ProtectedAdminRoute>
            } />
            <Route path="customers" element={
              <ProtectedAdminRoute routeKey="customers">
                <Customers />
              </ProtectedAdminRoute>
            } />
            <Route path="helpers" element={
              <ProtectedAdminRoute routeKey="helpers">
                <HelpersList />
              </ProtectedAdminRoute>
            } />
            <Route path="helper-visibility" element={
              <ProtectedAdminRoute routeKey="helper-visibility">
                <HelperVisibility />
              </ProtectedAdminRoute>
            } />
            <Route path="helpers/:helperId" element={
              <ProtectedAdminRoute routeKey="helpers/:helperId">
                <HelperDetail />
              </ProtectedAdminRoute>
            } />
            <Route path="helpers-kyc" element={
              <ProtectedAdminRoute routeKey="helpers-kyc">
                <HelperKYC />
              </ProtectedAdminRoute>
            } />
            <Route path="safeguarding" element={
              <ProtectedAdminRoute routeKey="safeguarding">
                <Safeguarding />
              </ProtectedAdminRoute>
            } />
            <Route path="tasks" element={
              <ProtectedAdminRoute routeKey="tasks">
                <TasksBookings />
              </ProtectedAdminRoute>
            } />
            <Route path="reports" element={
              <ProtectedAdminRoute routeKey="reports">
                <Reports />
              </ProtectedAdminRoute>
            } />
            <Route path="financial" element={
              <ProtectedAdminRoute routeKey="financial">
                <Financial />
              </ProtectedAdminRoute>
            } />
            <Route path="transactions" element={
              <ProtectedAdminRoute routeKey="transactions">
                <Transactions />
              </ProtectedAdminRoute>
            } />
            <Route path="payouts" element={
              <ProtectedAdminRoute routeKey="payouts">
                <Payouts />
              </ProtectedAdminRoute>
            } />
            <Route path="ledgers" element={
              <ProtectedAdminRoute routeKey="ledgers">
                <Ledgers />
              </ProtectedAdminRoute>
            } />
            <Route path="promo-codes" element={
              <ProtectedAdminRoute routeKey="promo-codes">
                <PromoCodes />
              </ProtectedAdminRoute>
            } />
            <Route path="audit-logs" element={
              <ProtectedAdminRoute routeKey="audit-logs">
                <AuditLogs />
              </ProtectedAdminRoute>
            } />
            <Route path="system-health" element={
              <ProtectedAdminRoute routeKey="system-health">
                <SystemHealth />
              </ProtectedAdminRoute>
            } />
            <Route path="disputes" element={
              <ProtectedAdminRoute routeKey="disputes">
                <Disputes />
              </ProtectedAdminRoute>
            } />
            <Route path="approvals-hub" element={
              <ProtectedAdminRoute routeKey="approvals-hub">
                <ApprovalsHub />
              </ProtectedAdminRoute>
            } />
            <Route path="automation" element={
              <ProtectedAdminRoute routeKey="automation">
                <AutomationMonitor />
              </ProtectedAdminRoute>
            } />
            <Route path="ai-agents" element={
              <ProtectedAdminRoute routeKey="ai-agents">
                <AiAgents />
              </ProtectedAdminRoute>
            } />
            <Route path="management" element={
              <ProtectedAdminRoute routeKey="management">
                <Management />
              </ProtectedAdminRoute>
            } />
            <Route path="privacy" element={
              <ProtectedAdminRoute routeKey="privacy">
                <PrivacyRegister />
              </ProtectedAdminRoute>
            } />
            <Route path="help-articles" element={
              <ProtectedAdminRoute routeKey="help-articles">
                <HelpArticles />
              </ProtectedAdminRoute>
            } />
            {/* UAT results read uat_* tables, which exist only in the separate
                UAT project — mount this route only in a UAT-enabled build. */}
            {UAT_ENABLED && (
              <Route path="uat-results" element={
                <ProtectedAdminRoute routeKey="uat-results">
                  <UATResults />
                </ProtectedAdminRoute>
              } />
            )}
            <Route path="trust-safety" element={
              <ProtectedAdminRoute routeKey="trust-safety">
                <TrustSafetyReports />
              </ProtectedAdminRoute>
            } />
            <Route path="client-flags" element={
              <ProtectedAdminRoute routeKey="client-flags">
                <ClientTrustFlags />
              </ProtectedAdminRoute>
            } />
            <Route path="marketing" element={
              <ProtectedAdminRoute routeKey="marketing">
                <div className="p-6">Marketing Module (Coming Soon)</div>
              </ProtectedAdminRoute>
            } />
            <Route path="support" element={
              <ProtectedAdminRoute routeKey="support">
                <AdminSupport />
              </ProtectedAdminRoute>
            } />
            <Route path="analytics" element={
              <ProtectedAdminRoute routeKey="analytics">
                <Analytics />
              </ProtectedAdminRoute>
            } />
            <Route path="daily-report" element={
              <ProtectedAdminRoute routeKey="daily-report">
                <DailyReport />
              </ProtectedAdminRoute>
            } />
            <Route path="logs" element={
              <ProtectedAdminRoute routeKey="logs">
                <AuditLogs />
              </ProtectedAdminRoute>
            } />
            <Route path="data-seeding" element={
              <ProtectedAdminRoute routeKey="data-seeding">
                <DataSeeding />
              </ProtectedAdminRoute>
            } />
            <Route path="funnel-analytics" element={
              <ProtectedAdminRoute routeKey="funnel-analytics">
                <FunnelAnalytics />
              </ProtectedAdminRoute>
            } />
            <Route path="error-logs" element={
              <ProtectedAdminRoute routeKey="error-logs">
                <ErrorLogs />
              </ProtectedAdminRoute>
            } />
            <Route path="settings" element={
              <ProtectedAdminRoute routeKey="settings">
                <Settings />
              </ProtectedAdminRoute>
            } />
            <Route path="account-security" element={
              <ProtectedAdminRoute routeKey="account-security">
                <AccountSecurity />
              </ProtectedAdminRoute>
            } />
          <Route path="categories" element={
            <ProtectedAdminRoute routeKey="categories">
              <Categories />
            </ProtectedAdminRoute>
          } />
          <Route path="images" element={
            <ProtectedAdminRoute routeKey="images">
              <AdminImages />
            </ProtectedAdminRoute>
          } />
                <Route path="operational-regions" element={
                  <ProtectedAdminRoute routeKey="operational-regions">
                    <OperationalRegions />
                  </ProtectedAdminRoute>
                } />
                <Route path="service-locations" element={
                  <ProtectedAdminRoute routeKey="service-locations">
                    <AdminServiceLocations />
                  </ProtectedAdminRoute>
                } />
                <Route path="integrity" element={
                  <ProtectedAdminRoute routeKey="integrity">
                    <AdminIntegrityDashboard />
                  </ProtectedAdminRoute>
                } />
              <Route path="pricing-strategy" element={
                <ProtectedAdminRoute routeKey="pricing-strategy">
                  <PricingStrategy />
                </ProtectedAdminRoute>
              } />
              <Route path="loyalty-cost" element={
                <ProtectedAdminRoute routeKey="loyalty-cost">
                  <LoyaltyCost />
                </ProtectedAdminRoute>
              } />
              <Route path="refund-policy" element={
                <ProtectedAdminRoute routeKey="refund-policy">
                  <RefundPolicy />
                </ProtectedAdminRoute>
              } />
              <Route path="outcome-templates" element={
                <ProtectedAdminRoute routeKey="outcome-templates">
                  <OutcomeTemplates />
                </ProtectedAdminRoute>
              } />
              <Route path="site-copy" element={
                <ProtectedAdminRoute routeKey="site-copy">
                  <ContentTranslations />
                </ProtectedAdminRoute>
              } />
              <Route path="document-renewals" element={
                <ProtectedAdminRoute routeKey="document-renewals">
                  <DocumentRenewals />
                </ProtectedAdminRoute>
              } />
              <Route path="screening" element={
                <ProtectedAdminRoute routeKey="screening">
                  <MediaScreening />
                </ProtectedAdminRoute>
              } />
              <Route path="work-photos" element={
                <ProtectedAdminRoute routeKey="work-photos">
                  <WorkPhotoModeration />
                </ProtectedAdminRoute>
              } />
              <Route path="appeals" element={
                <ProtectedAdminRoute routeKey="appeals">
                  <Appeals />
                </ProtectedAdminRoute>
              } />
              <Route path="profile-reports" element={
                <ProtectedAdminRoute routeKey="profile-reports">
                  <ProfileReports />
                </ProtectedAdminRoute>
              } />
              <Route path="cross-border-grants" element={
                <ProtectedAdminRoute routeKey="cross-border-grants">
                  <CrossBorderProviders />
                </ProtectedAdminRoute>
              } />
              <Route path="reviews" element={
                <ProtectedAdminRoute routeKey="reviews">
                  <AdminReviews />
                </ProtectedAdminRoute>
              } />
              <Route path="review-queue" element={
                <ProtectedAdminRoute routeKey="review-queue">
                  <ReviewQueue />
                </ProtectedAdminRoute>
              } />
              <Route path="feedback" element={
                <ProtectedAdminRoute routeKey="feedback">
                  <AdminAppFeedback />
                </ProtectedAdminRoute>
              } />
          <Route path="all-helpers" element={<Navigate to="/admin/helpers" replace />} />
          </Route>
          
          {/* Route aliases / redirects */}
          <Route path="/login" element={<Auth />} />
          <Route path="/signup" element={<Auth />} />
          <Route path="/search" element={<Navigate to="/booking-flow" replace />} />
          <Route path="/bookings" element={<Navigate to="/my-bookings" replace />} />
          <Route path="/helpers" element={<Navigate to="/booking-flow" replace />} />
          <Route path="/profile" element={<ProfileRedirect />} />
          {/* Find Jobs — archived job-board subsystem (see /jobs note above). */}
          <Route path="/find-jobs" element={<Navigate to="/" replace />} />
          {/* New pages */}
          <Route path="/share-story" element={<ShareStory />} />
          <Route path="/story-consent/:testimonialId" element={
            <ProtectedRoute requireCompleteProfile={false}>
              <StoryConsent />
            </ProtectedRoute>
          } />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          {/* The link in every offer Kanfy sends: no sign-in (20270548000000). */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/guidelines" element={<CommunityGuidelines />} />
          <Route path="/helper-resources" element={<HelperResources />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundaryWrapper>
      <OfflineBanner />
      {/* Not in the app (M9, §R.34): it talks about browser cookies, and the
          APK sends no analytics until the owner turns OpenPanel on there.
          With no recorded choice hasAnalyticsConsent() is false, so the app
          emits nothing; a native first-run consent screen comes with that
          switch-on. */}
      {!isNative && <CookieConsent />}
      {/* Once, for anyone signed in with no recorded choices (legal review
          §6, 20270580000000). Not over staff tools or the pages that set
          up an account or answer an unsubscribe link. */}
      {!pathname.startsWith('/admin') && !pathname.startsWith('/auth') && !pathname.startsWith('/unsubscribe') &&
        <FirstSignInChoices />}
      {!pathname.startsWith('/admin') &&
       !pathname.startsWith('/onboarding') &&
       !pathname.startsWith('/helper-profile') &&
       !pathname.startsWith('/client-profile') &&
       // #774/#775 — the PUBLIC profile route (/helper/:helperId, HelperProfile.tsx)
       // was never excluded here — only /helper-profile, the helper's OWN
       // profile-creation route (HelperProfileCreation), was. The FAB sat at
       // bottom-20 right-3, colliding with the sticky mobile Book Now bar this
       // page adds at the same screen edge. Scoped to just that route, not a
       // blanket /helper/ prefix — /helper/bookings and /helper/my-services
       // are unrelated helper-facing pages that should keep the widget.
       !/^\/helper\/(?!bookings\b|my-services\b)/.test(pathname) &&
       // The app shows no floating Help button: Profile has Help (owner,
       // 12 Oct). The panel stays mounted so those buttons can open it.
       <HelpWidget floating={!isNative} />}
    </>
  );
};

// Top-level router: /uat is rendered BEFORE AuthWrapper so it can never be
// intercepted or redirected by the app's auth middleware.
function AppRouter() {
  const location = useLocation();
  if (location.pathname.startsWith('/uat')) {
    return (
      <>
        <Toaster />
        <Sonner />
        <Suspense fallback={<LoadingFallback />}>
          <UATPortal />
        </Suspense>
      </>
    );
  }
  return (
    <AuthWrapper>
      <AppContent />
    </AuthWrapper>
  );
}

function App() {
  // Recover from chunk-load failures caused by new Vercel deployments.
  // Old hashed chunk filenames return a 404 HTML response, which React
  // reports as "text/html is not a valid JavaScript MIME type". Force-reload
  // to pick up the new chunks whenever this is detected.
  useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
      if (
        event.message?.includes('Failed to fetch dynamically imported module') ||
        event.message?.includes('is not a valid JavaScript MIME type')
      ) {
        // Guard against infinite reload loop on slow/offline connections:
        // allow exactly one auto-reload per session.
        if (sessionStorage.getItem('chunk_reload_attempted')) return;
        sessionStorage.setItem('chunk_reload_attempted', '1');
        window.location.reload();
      }
    };
    window.addEventListener('error', handleChunkError);
    return () => window.removeEventListener('error', handleChunkError);
  }, []);

  // App only (M10): queries pause on the device's connectivity, not the
  // WebView's navigator.onLine. A no-op on the website.
  wireNativeOnlineManager();
  const [queryClient] = useState(() => new QueryClient({
    /**
     * Every failed query is reported, app-wide.
     *
     * Three separate bugs this session shared one shape: a query failed, the
     * component destructured `data = []`, and the FAILURE rendered as an EMPTY
     * LIST — indistinguishable from "you have nothing". #682 showed a 45-deep
     * KYC backlog as "all caught up"; #674 showed a broken /my-helpers as "no
     * favourites"; AuditLogs showed every row's email as "N/A". None of them
     * logged anything, so none were noticed.
     *
     * Fixing that per-page is whack-a-mole. A cache-level handler makes the
     * next one visible the first time it happens, without touching 200 call
     * sites. It deliberately does NOT toast: many queries are intentionally
     * permission-gated and would spam the user. Surfacing an error to the USER
     * stays each page's job; surfacing it to US is this.
     */
    queryCache: new QueryCache({
      onError: (error, query) => {
        // logger.error(message, error) only prints an ERROR's .message in
        // production — passing a plain object logged "Unknown error" and threw
        // away the queryKey and PostgREST code, making this dev-only. The
        // identifying detail goes in the MESSAGE; the raw error goes second.
        const code = (error as { code?: string })?.code;
        logger.error(
          `[query-failed] ${JSON.stringify(query.queryKey)}${code ? ` code=${code}` : ''}`,
          error,
        );
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 2, // 2 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes cache
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: 'always',
        retry: 1,
        retryDelay: 500,
      },
    },
  }));

  return (
    <ThemeProvider>
        <ErrorBoundaryWrapper componentName="App">
      <QueryClientProvider client={queryClient}>
        <PerformanceMonitor />
        <BrowserRouter>
          <ScrollToTop />
          <LanguageProvider>
            <ContentOverridesProvider>
              <TooltipProvider>
                <ModeSwitchProvider>
                  <AppRouter />
                </ModeSwitchProvider>
              </TooltipProvider>
            </ContentOverridesProvider>
          </LanguageProvider>
        </BrowserRouter>
      </QueryClientProvider>
        </ErrorBoundaryWrapper>
    </ThemeProvider>
  );
}

export default App;
